(function () {
  'use strict';

  if (customElements.get('bombae-buy-buttons-form')) return;

  const DEBOUNCE_MS = 350;
  const subscribers = new Set();
  const cartState = {
    loaded: false,
    cart: null,
    byVariantId: new Map(),
    pending: new Map(),
    cartPromise: null,
  };

  function emitChange() {
    subscribers.forEach((callback) => callback());
  }

  function subscribeStore(callback) {
    subscribers.add(callback);
    return function unsubscribe() {
      subscribers.delete(callback);
    };
  }

  function getPendingState(variantId) {
    const key = String(variantId);

    if (!cartState.pending.has(key)) {
      cartState.pending.set(key, {
        desiredQty: null,
        timerId: null,
        syncing: false,
      });
    }

    return cartState.pending.get(key);
  }

  function buildVariantMap(cart) {
    const nextMap = new Map();

    (cart?.items || []).forEach((item) => {
      const variantId = String(item.variant_id);
      const existing = nextMap.get(variantId);

      if (existing) {
        existing.quantity += item.quantity;
        existing.lineKey = existing.lineKey || item.key;
      } else {
        nextMap.set(variantId, {
          quantity: item.quantity,
          lineKey: item.key,
        });
      }
    });

    cartState.cart = cart;
    cartState.byVariantId = nextMap;
    cartState.loaded = true;
  }

  async function fetchCart() {
    if (cartState.cartPromise) return cartState.cartPromise;

    cartState.cartPromise = fetch(`${window.routes.cart_url}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to load cart');
        }

        return response.json();
      })
      .then((cart) => {
        buildVariantMap(cart);
        emitChange();
        return cart;
      })
      .finally(() => {
        cartState.cartPromise = null;
      });

    return cartState.cartPromise;
  }

  function getRequestedSections() {
    const sections = ['cart-icon-bubble'];

    if (document.querySelector('cart-drawer')) {
      sections.unshift('cart-drawer');
    }

    return sections;
  }

  function updateCartIconBubble(html) {
    if (!html) return;

    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector('.shopify-section');
    bubble.innerHTML = source ? source.innerHTML : parsed.body.innerHTML;
  }

  function applySectionResponse(response) {
    if (!response?.sections) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && response.sections['cart-drawer']) {
      cartDrawer.renderContents({ sections: response.sections });
      return;
    }

    updateCartIconBubble(response.sections['cart-icon-bubble']);
  }

  function getServerQuantity(variantId) {
    return cartState.byVariantId.get(String(variantId))?.quantity || 0;
  }

  function getLineKey(variantId) {
    return cartState.byVariantId.get(String(variantId))?.lineKey || null;
  }

  async function requestAdd(variantId, quantity) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);
    formData.append('sections', getRequestedSections());
    formData.append('sections_url', window.location.pathname);

    const config = fetchConfig();
    config.headers.Accept = 'application/json';
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    const response = await fetch(window.routes.cart_add_url, config);
    const data = await response.json();

    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || data.errors || 'Unable to add item to cart');
    }

    applySectionResponse(data);
    await fetchCart();

    return cartState.cart;
  }

  async function requestChange(variantId, quantity) {
    let lineKey = getLineKey(variantId);

    if (!lineKey) {
      await fetchCart();
      lineKey = getLineKey(variantId);
    }

    if (!lineKey && quantity > 0) {
      return requestAdd(variantId, quantity);
    }

    if (!lineKey) {
      buildVariantMap(cartState.cart || { items: [] });
      emitChange();
      return cartState.cart;
    }

    const config = fetchConfig();
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify({
      id: lineKey,
      quantity,
      sections: getRequestedSections(),
      sections_url: window.location.pathname,
    });

    const response = await fetch(window.routes.cart_change_url, config);
    const data = await response.json();

    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || data.errors || 'Unable to update cart');
    }

    applySectionResponse(data);
    buildVariantMap(data);
    emitChange();

    return data;
  }

  async function syncVariant(variantId, desiredQty) {
    const currentQty = getServerQuantity(variantId);

    if (desiredQty === currentQty) {
      return cartState.cart;
    }

    if (desiredQty <= 0) {
      if (currentQty <= 0) return cartState.cart;
      return requestChange(variantId, 0);
    }

    if (currentQty <= 0) {
      return requestAdd(variantId, desiredQty);
    }

    return requestChange(variantId, desiredQty);
  }

  function publishCartUpdate(source, cartData, variantId) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source,
      cartData,
      variantId,
    });
  }

  function scheduleSync(variantId, delay) {
    const pending = getPendingState(variantId);

    if (pending.timerId) {
      window.clearTimeout(pending.timerId);
      pending.timerId = null;
    }

    if (pending.syncing) {
      emitChange();
      return;
    }

    pending.timerId = window.setTimeout(function () {
      flushVariant(variantId);
    }, delay);
  }

  async function flushVariant(variantId) {
    const pending = getPendingState(variantId);

    if (pending.syncing) return;

    const serverQty = getServerQuantity(variantId);
    const desiredQty = pending.desiredQty;

    pending.timerId = null;

    if (desiredQty == null || desiredQty === serverQty) {
      pending.desiredQty = null;
      emitChange();
      return;
    }

    pending.syncing = true;
    emitChange();

    try {
      const cart = await syncVariant(variantId, desiredQty);
      publishCartUpdate('bombae-buy-buttons', cart, variantId);
    } catch (error) {
      console.error(error);
      await fetchCart();
      pending.desiredQty = null;
    } finally {
      pending.syncing = false;

      const latestServerQty = getServerQuantity(variantId);
      if (pending.desiredQty === latestServerQty) {
        pending.desiredQty = null;
      } else if (pending.desiredQty != null) {
        scheduleSync(variantId, 0);
      }

      emitChange();
    }
  }

  function setDesiredQuantity(variantId, quantity) {
    const pending = getPendingState(variantId);
    pending.desiredQty = Math.max(0, quantity);
    scheduleSync(variantId, DEBOUNCE_MS);
    emitChange();
  }

  function getVariantViewState(variantId) {
    const pending = getPendingState(variantId);
    const serverQty = getServerQuantity(variantId);
    const quantity = pending.desiredQty == null ? serverQty : pending.desiredQty;

    return {
      quantity,
      syncing: pending.syncing,
    };
  }

  class BombaeBuyButtonsForm extends HTMLElement {
    constructor() {
      super();

      this.render = this.render.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleQtyClick = this.handleQtyClick.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
    }

    connectedCallback() {
      this.productForm = this.closest('product-form');
      this.form = this.productForm?.querySelector('form');
      this.variantInput = this.productForm?.querySelector('[name="id"]');
      this.addButton = this.querySelector('[type="submit"]');
      this.qtySelector = this.querySelector('[data-role="quantity-selector"]');
      this.quantityValue = this.querySelector('[data-role="quantity-value"]');
      this.minusButton = this.querySelector('[data-action="decrease"]');
      this.plusButton = this.querySelector('[data-action="increase"]');
      this.optimisticQuantity = null;
      this.awaitingSubmit = false;

      if (!this.addButton || !this.qtySelector || !this.quantityValue) return;

      this.unsubscribeStore = subscribeStore(this.render);
      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit, true);
      }
      if (this.variantInput) {
        this.variantInput.addEventListener('change', this.handleVariantChange);
      }

      this.addEventListener('click', this.handleQtyClick);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
      }

      this.buttonObserver = new MutationObserver(this.render);
      this.buttonObserver.observe(this.addButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
      });

      this.render();
      fetchCart().catch((error) => console.error(error));
    }

    disconnectedCallback() {
      this.unsubscribeStore?.();
      this.unsubscribeCartUpdate?.();
      this.buttonObserver?.disconnect();
      if (this.form) {
        this.form.removeEventListener('submit', this.handleSubmit, true);
      }
      if (this.variantInput) {
        this.variantInput.removeEventListener('change', this.handleVariantChange);
      }
    }

    get currentVariantId() {
      return this.variantInput?.value || '';
    }

    get submittedQuantity() {
      const rawValue = this.form?.querySelector('[name="quantity"]')?.value;
      const parsedValue = parseInt(rawValue, 10);
      return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 1;
    }

    handleSubmit() {
      if (!this.currentVariantId) return;
      if (this.addButton.hasAttribute('disabled') || this.addButton.getAttribute('aria-disabled') === 'true') return;

      const currentQuantity = getServerQuantity(this.currentVariantId);
      this.optimisticQuantity = currentQuantity + this.submittedQuantity;
      this.awaitingSubmit = true;
      this.render();
    }

    handleVariantChange() {
      this.optimisticQuantity = null;
      this.awaitingSubmit = false;
      this.render();
      fetchCart().catch((error) => console.error(error));
    }

    handleCartUpdate(event) {
      if (event?.source === 'bombae-buy-buttons') return;
      fetchCart()
        .then(() => {
          this.optimisticQuantity = null;
          this.awaitingSubmit = false;
          this.render();
        })
        .catch((error) => console.error(error));
    }

    async handleQtyClick(event) {
      const actionButton = event.target.closest('[data-action]');
      if (!actionButton || !this.currentVariantId) return;
      if (actionButton.type === 'submit') return;

      event.preventDefault();

      if (!cartState.loaded) {
        try {
          await fetchCart();
        } catch (error) {
          console.error(error);
          return;
        }
      }

      const viewState = getVariantViewState(this.currentVariantId);
      let nextQuantity = viewState.quantity;

      if (actionButton.dataset.action === 'increase') {
        nextQuantity = viewState.quantity + 1;
      } else if (actionButton.dataset.action === 'decrease') {
        nextQuantity = Math.max(0, viewState.quantity - 1);
      } else {
        return;
      }

      setDesiredQuantity(this.currentVariantId, nextQuantity);
    }

    render() {
      if (!this.addButton || !this.qtySelector || !this.quantityValue) return;

      const variantId = this.currentVariantId;
      const viewState = variantId ? getVariantViewState(variantId) : { quantity: 0, syncing: false };
      const isButtonLoading =
        this.addButton.classList.contains('loading') || this.addButton.getAttribute('aria-disabled') === 'true';
      const effectiveQuantity =
        this.awaitingSubmit && this.optimisticQuantity != null ? this.optimisticQuantity : viewState.quantity;
      const hasQuantity = effectiveQuantity > 0;
      const soldOutOrUnavailable =
        this.addButton.hasAttribute('disabled') || this.addButton.getAttribute('aria-disabled') === 'true';
      const isSyncing = viewState.syncing || this.awaitingSubmit;

      if (this.awaitingSubmit && !isButtonLoading && getServerQuantity(variantId) === 0) {
        this.awaitingSubmit = false;
        this.optimisticQuantity = null;
      }

      if (this.awaitingSubmit && getServerQuantity(variantId) > 0) {
        this.awaitingSubmit = false;
        this.optimisticQuantity = null;
      }

      this.dataset.state = hasQuantity ? 'quantity' : 'button';
      this.classList.toggle('is-syncing', isSyncing);
      this.addButton.hidden = hasQuantity;
      this.qtySelector.hidden = !hasQuantity;
      this.qtySelector.classList.toggle('bombae-qty-pill--loading', isSyncing);
      this.quantityValue.textContent = String(effectiveQuantity);

      if (this.minusButton) {
        this.minusButton.disabled = isSyncing || effectiveQuantity <= 0;
      }

      if (this.plusButton) {
        this.plusButton.disabled = isSyncing || soldOutOrUnavailable;
      }
    }
  }

  customElements.define('bombae-buy-buttons-form', BombaeBuyButtonsForm);
})();
